import { z } from "zod";

// =========================================================================
// ENUMS GLOBALES
// =========================================================================

export const CurrencyCodeSchema = z.enum(["USD", "COP", "MXN", "PEN", "ARS"]);

export const CountryCodeSchema = z.string().length(2).toUpperCase();

// =========================================================================
// PHONE VALIDATION HELPERS
// =========================================================================

/**
 * Valida formato E.164 internacional
 * Ejemplo: +573001234567
 */
export const E164PhoneSchema = z
  .string()
  .regex(/^\+[1-9]\d{10,14}$/, "Formato inválido (ej: +573001234567)")
  // Validación fina por país
  .superRefine((val, ctx) => {
    // 🇨🇴 COLOMBIA (+57): Total 13 caracteres (+57 + 10 dígitos)
    if (val.startsWith("+57") && val.length !== 13) {
      ctx.addIssue({
        code: "custom",
        message: "El celular en Colombia debe tener 10 dígitos",
      });
      return;
    }

    // 🇲🇽 MÉXICO (+52): Total 13 caracteres (+52 + 10 dígitos)
    if (val.startsWith("+52") && val.length !== 13) {
      ctx.addIssue({
        code: "custom",
        message: "El celular en México debe tener 10 dígitos",
      });
      return;
    }

    // 🇺🇸 USA (+1): Total 12 caracteres (+1 + 10 dígitos)
    if (val.startsWith("+1") && val.length !== 12) {
      ctx.addIssue({
        code: "custom",
        message: "El número en USA debe tener 10 dígitos",
      });
      return;
    }

    // 🇵🇪 PERÚ (+51): Total 12 caracteres (+51 + 9 dígitos)
    if (val.startsWith("+51") && val.length !== 12) {
      ctx.addIssue({
        code: "custom",
        message: "El celular en Perú debe tener 9 dígitos",
      });
      return;
    }
  });

/**
 * Validación específica para Colombia
 * Acepta: 3001234567 o +573001234567
 */
export const ColombiaPhoneSchema = z
  .string()
  .regex(/^(\+57)?3[0-9]{9}$/, "Número colombiano inválido")
  .transform((val) => (val.startsWith("+57") ? val : `+57${val}`));

/**
 * Validación específica para México
 * Acepta: 5512345678 o +525512345678
 */
export const MexicoPhoneSchema = z
  .string()
  .regex(/^(\+52)?[1-9][0-9]{9}$/, "Número mexicano inválido")
  .transform((val) => (val.startsWith("+52") ? val : `+52${val}`));

// =========================================================================
// ADDRESS SCHEMA
// =========================================================================

export const AddressSchema = z.object({
  address1: z.string().min(5, "Dirección muy corta"),
  address2: z.string().optional(),
  city: z.string().min(2),
  province: z.string().min(2),
  province_code: z.string().min(1),
  zip: z.string().optional(),
  country_code: CountryCodeSchema,

  // IDs Geográficos (DANE para Colombia, equivalentes para otros países)
  region_id: z.string().optional(),
  city_id: z.string().optional(),
});

// =========================================================================
// CUSTOMER SCHEMA
// =========================================================================

export const CustomerSchema = z.object({
  email: z
    .email("Email inválido")
    .optional()
    .or(z.literal(""))
    .transform((val) => val || undefined),

  phone: E164PhoneSchema,

  first_name: z.string().min(2, "Nombre muy corto"),
  last_name: z.string().min(2, "Apellido muy corto"),

  marketing_opt_in: z.boolean().default(false),

  address: AddressSchema,

  // Campos extras definidos en el editor (JSONB en BD)
  extra_fields: z.record(z.string(), z.any()).optional(),
});

export const LeadCustomerSchema = CustomerSchema.extend({
  // Allow empty string (widget sends "" when field is blank) and transform to undefined
  phone: E164PhoneSchema.optional()
    .or(z.literal(""))
    .transform((v) => v || undefined),
  first_name: z.string().min(2, "Nombre muy corto").optional(),
  last_name: z.string().min(2, "Apellido muy corto").optional(),
  // Discard the address object when address1 is absent, empty, or too short.
  // The widget sends placeholder values like "N/A" when no address is collected.
  address: z.preprocess((val) => {
    if (val !== null && typeof val === "object") {
      const a = val as Record<string, unknown>;
      const address1 = typeof a.address1 === "string" ? a.address1.trim() : "";
      if (address1.length < 5) return undefined;
    }
    return val;
  }, AddressSchema.optional()),
}).superRefine((data, ctx) => {
  if (!data.phone && !data.email) {
    ctx.addIssue({
      code: "custom",
      message: "Al menos un método de contacto (phone o email) es requerido",
      path: ["phone"],
    });
  }
});

// =========================================================================
// MONEY SCHEMA
// =========================================================================

export const MoneySchema = z.object({
  amount: z.number().nonnegative("El monto no puede ser negativo"),
  currency: CurrencyCodeSchema,
});

// =========================================================================
// PAGINATION SCHEMA
// =========================================================================

export const PaginationParamsSchema = z.object({
  page: z.coerce.number().int().positive().default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
});

export const PaginationMetaSchema = z.object({
  page: z.number().int().positive(),
  limit: z.number().int().positive(),
  total: z.number().int().nonnegative(),
  total_pages: z.number().int().nonnegative(),
  has_next: z.boolean(),
  has_prev: z.boolean(),
});

// =========================================================================
// SHIPPING RULES SCHEMA
// =========================================================================

export const ShippingRuleSchema = z.object({
  id: z.string().optional(),
  name: z.string().min(1),
  description: z.string().optional(),
  active: z.boolean().default(true),
  sort_order: z.number().int().nonnegative(),
  action: z.discriminatedUnion("type", [
    z.object({ type: z.literal("free_shipping") }),
    z.object({ type: z.literal("flat_rate"), cost: z.number().nonnegative() }),
    z.object({
      type: z.literal("percentage_off"),
      percentage: z.number().min(0).max(100),
    }),
  ]),
  conditions: z
    .object({
      min_subtotal: z.number().nonnegative().optional(),
      max_subtotal: z.number().nonnegative().optional().nullable(),
      allowed_state_codes: z.array(z.string()).optional(),
    })
    .optional(),
});

// =========================================================================
// ERROR RESPONSE SCHEMA
// =========================================================================

export const ErrorResponseSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    details: z.record(z.string(), z.any()).optional(),
  }),
  request_id: z.string(),
  trace_id: z.string(),
  timestamp: z.iso.datetime(),
});

// =========================================================================
// SUCCESS META SCHEMA
// =========================================================================

export const SuccessMetaSchema = z.object({
  status: z.enum(["success", "accepted", "processing"]),
  processing_time_ms: z.number().nonnegative().optional(),
  request_id: z.string(),
  trace_id: z.string(),
  timestamp: z.iso.datetime(),
});

// =========================================================================
// TYPE EXPORTS
// =========================================================================

export type Address = z.infer<typeof AddressSchema>;
export type Customer = z.infer<typeof CustomerSchema>;
export type LeadCustomer = z.infer<typeof LeadCustomerSchema>;

export type Money = z.infer<typeof MoneySchema>;
export type PaginationParams = z.infer<typeof PaginationParamsSchema>;
export type PaginationMeta = z.infer<typeof PaginationMetaSchema>;
export type ErrorResponse = z.infer<typeof ErrorResponseSchema>;
export type SuccessMeta = z.infer<typeof SuccessMetaSchema>;
export type ShippingRule = z.infer<typeof ShippingRuleSchema>;
